import React, { useState } from 'react';
import { useNightAuditAdmin } from '../../hooks/useNightAuditAdmin';
import { ROLES } from '../../utils/rbac';
import styles from './AdminSection.module.css';

const AdminSection = ({ currentUser }) => {
  const { audits, isLoading, isDeleting, error, deleteAudit } = useNightAuditAdmin();
  const [deletingId, setDeletingId] = useState(null);

  const isSuperAdmin = currentUser?.role === ROLES.SUPERADMIN;

  const handleDelete = async (audit) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the night audit for ${formatDate(audit.auditDate)}?\n\n` +
      `This action is PERMANENT and cannot be undone.\n` +
      `All associated audit logs will also be deleted.`
    );

    if (!confirmed) return;

    setDeletingId(audit.id);
    await deleteAudit(audit.id);
    setDeletingId(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'COMPLETED':
        return styles.statusCompleted;
      case 'IN_PROGRESS':
        return styles.statusInProgress;
      case 'INCOMPLETE':
        return styles.statusIncomplete;
      default:
        return '';
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Audit History</h3>
        </div>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading audit history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Audit History</h3>
        </div>
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (audits.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3 className={styles.title}>Audit History</h3>
        </div>
        <div className={styles.empty}>
          <p>No previous audits found</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Audit History</h3>
        <span className={styles.count}>Total: {audits.length}</span>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Audit Date</th>
              <th>Status</th>
              <th>Catch-up</th>
              <th>Started At</th>
              <th>Completed At</th>
              <th>Duration</th>
              {isSuperAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {audits.map((audit) => {
              const duration = audit.startedAt && audit.completedAt
                ? Math.round((new Date(audit.completedAt) - new Date(audit.startedAt)) / 60000)
                : null;

              return (
                <tr key={audit.id}>
                  <td className={styles.dateCell}>
                    {formatDate(audit.auditDate)}
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusClass(audit.status)}`}>
                      {audit.status}
                    </span>
                  </td>
                  <td>
                    <span className={audit.isCatchupMode ? styles.catchupYes : styles.catchupNo}>
                      {audit.isCatchupMode ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className={styles.dateTimeCell}>
                    {formatDateTime(audit.startedAt)}
                  </td>
                  <td className={styles.dateTimeCell}>
                    {formatDateTime(audit.completedAt)}
                  </td>
                  <td className={styles.durationCell}>
                    {duration ? `${duration} min` : '-'}
                  </td>
                  {isSuperAdmin && (
                    <td>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(audit)}
                        disabled={deletingId === audit.id || isDeleting}
                      >
                        {deletingId === audit.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSection;
